import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Grid, Paper, FormControl, InputLabel, Select, MenuItem, alpha, IconButton, Chip, Tooltip, CircularProgress, Stack, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Event, EventBusy, AccessTime, KeyboardArrowLeft, KeyboardArrowRight, LocationOn, LocationOff, People, PersonPinOutlined, CheckCircle } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';
import Loader from '../../common/spinner';
import StatsCard from './StatsCard';

dayjs.extend(utc);
dayjs.extend(timezone);

const formatStatus = (status) => {
    if (!status) return 'No Data';
    switch (status.toLowerCase()) {
        case 'present': return 'Present';
        case 'absent': return 'Absent';
        case 'latearrival': case 'late': return 'Late Arrival';
        case 'leave': return 'Leave';
        case 'weekend': return 'Weekend';
        case 'holiday': return 'Holiday';
        default: return status;
    }
};

function MyAttendance({ empId }) {
    const theme = useTheme();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(dayjs().startOf('month'));
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [monthlyAttendance, setMonthlyAttendance] = useState({});
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [monthlyOverview, setMonthlyOverview] = useState({
        present: 0, absent: 0, late: 0, leaves: 0, totalWorkingDays: 0, attendancePercentage: 0
    });

    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = empId ?? userData?.id;
    const orgId = userData?.organization_id;
    const locId = userData?.location_id;
    const isAttendancePath = location.pathname === '/employee/my-attendance';

    const fetchAttendanceData = useCallback(async () => {
        setLoading(true);
        const monthNumeric = selectedDate.month() + 1;
        const year = selectedDate.year();
        const monthName = selectedDate.format('MMMM');
        const today = dayjs().startOf('day');

        try {
            const holidayUrl = API_ENDPOINTS.getHolidaysByYearAndMonth(year, orgId, locId, monthNumeric);
            const attendanceUrl = API_ENDPOINTS.getAttendanceByEmployeeAndStatus(userId, monthName, year);

            const [attendanceResponse, holidayResponse] = await Promise.all([
                getData(attendanceUrl),
                getData(holidayUrl)
            ]);

            const holidayList = holidayResponse?.message?.holidays || [];
            setHolidays(holidayList);

            const transformedData = {};
            let presentCount = 0;
            let absentCount = 0;
            let lateCount = 0;
            let leavesCount = 0;

            const records = attendanceResponse?.message?.attendanceRecords || [];

            // 1. Map existing API records
            records.forEach(item => {
                const day = dayjs(item.date).date();
                transformedData[day] = {
                    date: item.date,
                    inTime: item.check_in ? dayjs(item.check_in).format('HH:mm') : '--:--',
                    outTime: item.check_out ? dayjs(item.check_out).format('HH:mm') : '--:--',
                    status: item.status,
                    late: item.late_minutes ? `${item.late_minutes} Min` : null,
                    inOffice: item.office_premises?.check_in?.in_office === 'yes',
                    outOffice: item.office_premises?.check_out?.in_office === 'yes',
                    hasData: true
                };
            });

            // 2. Fill gaps and calculate stats
            const daysInMonth = selectedDate.daysInMonth();
            for (let d = 1; d <= daysInMonth; d++) {
                const currentLoopDate = selectedDate.date(d);
                const isWeekend = currentLoopDate.day() === 0 || currentLoopDate.day() === 6;
                const isPastDate = currentLoopDate.isBefore(today, 'day');
                const isHolidayDate = holidayList.some(h => dayjs(h.date).isSame(currentLoopDate, 'day'));

                if (!transformedData[d]) {
                    let derivedStatus = '';
                    if (isHolidayDate) derivedStatus = 'Holiday';
                    else if (isWeekend) derivedStatus = 'Weekend';
                    else if (isPastDate) {
                        derivedStatus = 'Absent';
                        absentCount++;
                    }

                    transformedData[d] = {
                        status: derivedStatus,
                        hasData: false,
                        inTime: '--:--',
                        outTime: '--:--'
                    };
                } else {
                    const statusLower = transformedData[d].status?.toLowerCase();
                    if (statusLower === 'present') presentCount++;
                    else if (statusLower === 'latearrival' || statusLower === 'late') lateCount++;
                    else if (statusLower === 'leave') leavesCount++;
                    else if (statusLower === 'absent') absentCount++;
                }
            }

            const totalWorkingDays = Object.values(transformedData).filter(d => d.status !== 'Weekend' && d.status !== 'Holiday').length;
            const attendancePercentage = totalWorkingDays > 0
                ? (((presentCount + lateCount) / totalWorkingDays) * 100).toFixed(1)
                : 0;

            setMonthlyAttendance(transformedData);
            setMonthlyOverview({
                present: presentCount, absent: absentCount, late: lateCount,
                leaves: leavesCount, totalWorkingDays, attendancePercentage
            });
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [selectedDate, userId, orgId, locId]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    const getStatusStyles = (status, isWeekend, holiday) => {
        if (holiday) return { bgcolor: alpha(theme.palette.secondary.main, 0.1), border: `1px solid ${theme.palette.secondary.light}` };
        if (isWeekend) return { bgcolor: alpha(theme.palette.action.disabledBackground, 0.05), border: `1px solid ${theme.palette.divider}` };

        switch (status?.toLowerCase()) {
            case 'present': return { bgcolor: '#f1f8f1', border: '1px solid #c8e6c9' };
            case 'absent': return { bgcolor: '#fff5f5', border: '1px solid #ffcdd2' };
            case 'latearrival': case 'late': return { bgcolor: '#fff9f1', border: '1px solid #ffe0b2' };
            case 'leave': return { bgcolor: '#f0f7ff', border: '1px solid #b3e5fc' };
            default: return { bgcolor: 'white', border: `1px solid ${theme.palette.divider}` };
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'present': return '#2e7d32';
            case 'absent': return theme.palette.error.main;
            case 'latearrival': case 'late': return theme.palette.warning.main;
            case 'leave': return theme.palette.info.main;
            case 'weekend': return theme.palette.grey[400];
            case 'holiday': return theme.palette.secondary.main;
            default: return theme.palette.grey[300];
        }
    };

    const getStatusIcon = (status) => {
        const size = { fontSize: 20 };
        switch (status?.toLowerCase()) {
            case 'present': return <CheckCircle sx={{ color: '#2e7d32', ...size }} />;
            case 'latearrival': case 'late': return <AccessTime sx={{ color: '#ed6c02', ...size }} />;
            case 'absent': return <EventBusy sx={{ color: '#d32f2f', ...size }} />;
            case 'leave': return <PersonPinOutlined sx={{ color: '#0288d1', ...size }} />;
            default: return null;
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', background: alpha(theme.palette.primary.light, 0.05) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">My Attendance</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            views={['year', 'month']}
                            label="Month and Year"
                            value={selectedDate}
                            onChange={(val) => setSelectedDate(val.startOf('month'))}
                            slotProps={{ textField: { size: 'small', sx: { width: 185 } } }}
                        />
                    </LocalizationProvider>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={selectedStatus} label="Status" onChange={(e) => setSelectedStatus(e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                            <MenuItem value="LateArrival">Late</MenuItem>
                            <MenuItem value="Leave">Leave</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            <Grid container spacing={3} mb={4}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatsCard title="Total Days" value={monthlyOverview.totalWorkingDays} icon={<People />} color="#6366F1" />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatsCard title="Present" value={monthlyOverview.present} icon={<PersonPinOutlined />} color="#10B981" />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatsCard title="Late Arrival" value={monthlyOverview.late} icon={<AccessTime />} color="#F59E0B" />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatsCard title="Absent" value={monthlyOverview.absent} icon={<EventBusy />} color="#EF4444" />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <StatsCard title="Leaves" value={monthlyOverview.leaves} icon={<PersonPinOutlined />} color="#3B82F6" />
                </Grid>
            </Grid>

            <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 4, position: 'relative' }}>
                {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 2, borderRadius: 4 }}>
                        {isAttendancePath ? <Loader /> : <CircularProgress />}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">{selectedDate.format('MMMM YYYY')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={() => setSelectedDate(selectedDate.subtract(1, 'month'))} sx={{ border: '1px solid #ddd' }}><KeyboardArrowLeft /></IconButton>
                        <IconButton size="small" onClick={() => setSelectedDate(selectedDate.add(1, 'month'))} sx={{ border: '1px solid #ddd' }}><KeyboardArrowRight /></IconButton>
                    </Box>
                </Box>

                {(() => {
                    const startDay = selectedDate.startOf('month').day();
                    const daysInMonth = selectedDate.daysInMonth();
                    const today = dayjs().startOf('day');

                    const dayItems = [];
                    for (let d = 1; d <= daysInMonth; d++) {
                        const currentLoopDate = selectedDate.date(d);
                        const dayRecord = monthlyAttendance[d];
                        const holiday = holidays.find(h => dayjs(h.date).isSame(currentLoopDate, 'day'));
                        const isWeekend = currentLoopDate.day() === 0 || currentLoopDate.day() === 6;
                        const isToday = dayjs().isSame(currentLoopDate, 'day');
                        const isFuture = currentLoopDate.isAfter(today, 'day');

                        let status = dayRecord?.status || '';
                        if (holiday) status = 'Holiday';

                        const color = getStatusColor(status);
                        const styleSet = getStatusStyles(status, isWeekend, !!holiday);
                        const isFiltered = selectedStatus !== 'all' &&
                            status?.toLowerCase() !== selectedStatus.toLowerCase() &&
                            !holiday;

                        dayItems.push({ d, currentLoopDate, dayRecord, holiday, isWeekend, isToday, isFuture, status, color, styleSet, isFiltered });
                    }

                    return (
                        <>
                            {/* Calendar grid - md and up */}
                            <Grid container spacing={1} columns={7} sx={{ display: { xs: 'none', md: 'flex' } }}>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                    <Grid size={{ xs: 1 }} key={d} sx={{ textAlign: 'center', fontWeight: 'bold', color: 'text.secondary', py: 1 }}>{d}</Grid>
                                ))}
                                {Array.from({ length: startDay }).map((_, i) => (
                                    <Grid size={{ xs: 1 }} key={`pad-${i}`}><Box sx={{ minHeight: 120, bgcolor: alpha('#000', 0.01), borderRadius: 2 }} /></Grid>
                                ))}
                                {dayItems.map(({ d, dayRecord, holiday, isWeekend, isToday, isFuture, status, color, styleSet, isFiltered }) => (
                                    <Grid size={{ xs: 1 }} key={d} sx={{ minWidth: 0 }}>
                                        <Tooltip title={holiday ? holiday.holiday_name : formatStatus(status)}>
                                            <Box sx={{
                                                minHeight: 125,
                                                minWidth: 0,
                                                border: `1px solid ${theme.palette.divider}`,
                                                ...(holiday ? styleSet : (isFuture ? { bgcolor: 'white' } : styleSet)),
                                                borderRadius: 2,
                                                p: 1,
                                                opacity: isFiltered ? 0.2 : 1,
                                                display: 'flex', flexDirection: 'column',
                                                transition: 'all 0.2s',
                                                '&:hover': { transform: isFiltered ? 'none' : 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body2" sx={{
                                                        fontWeight: 'bold', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                                                        bgcolor: isToday ? theme.palette.primary.main : 'transparent',
                                                        color: isToday ? 'white' : (holiday ? theme.palette.secondary.main : isWeekend ? theme.palette.text.disabled : 'text.primary'),
                                                        fontSize: '0.75rem',
                                                    }}>
                                                        {d}
                                                    </Typography>
                                                    {!isFuture && !isWeekend && !holiday && getStatusIcon(status)}
                                                </Box>

                                                <Box sx={{ mt: 1, borderTop: (status.toLowerCase() === 'present' && !holiday) ? '1px dashed #c8e6c9' : 'none', pt: 0.5, minWidth: 0 }}>
                                                    {holiday ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.secondary.main,
                                                                fontSize: '0.68rem',
                                                                fontWeight: 'bold',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {holiday.holiday_name}
                                                        </Typography>
                                                    ) : (!isFuture && !isWeekend && status) && (
                                                        <Chip label={formatStatus(status)} size="small" sx={{ fontSize: '0.7rem', height: 18, bgcolor: alpha(color, 0.1), color: color, fontWeight: 'bold', px: 1 }} />
                                                    )}
                                                </Box>

                                                {!isFuture && !isWeekend && !holiday && (
                                                    <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {/* Check-In Row */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            bgcolor: '#e8f5e9',
                                                            color: '#2e7d32',
                                                            px: 0.5,
                                                            py: 0.1,
                                                            borderRadius: '4px',
                                                            border: '1px solid #c8e6c9'
                                                        }}>
                                                            {dayRecord?.inOffice ? (
                                                                <LocationOn sx={{ fontSize: 10 }} />
                                                            ) : (
                                                                <LocationOff sx={{ fontSize: 10, color: theme.palette.error.main }} />
                                                            )}
                                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                                                {dayRecord?.inTime || '--:--'}
                                                            </Typography>
                                                        </Box>

                                                        {/* Check-Out Row */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            bgcolor: dayRecord?.outTime === '--:--' ? '#ffebee' : '#e8f5e9',
                                                            color: dayRecord?.outTime === '--:--' ? '#d32f2f' : '#2e7d32',
                                                            px: 0.5,
                                                            py: 0.1,
                                                            borderRadius: '4px',
                                                            border: dayRecord?.outTime === '--:--' ? '1px solid #ffcdd2' : '1px solid #c8e6c9'
                                                        }}>
                                                            {dayRecord?.outOffice ? (
                                                                <LocationOn sx={{ fontSize: 10 }} />
                                                            ) : (
                                                                <LocationOff sx={{ fontSize: 10, color: dayRecord?.outTime === '--:--' ? 'inherit' : theme.palette.error.main }} />
                                                            )}
                                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                                                {dayRecord?.outTime || '--:--'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Day list - below md: flat rows separated by real dividers, no per-row boxes */}
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                {dayItems.map(({ d, currentLoopDate, dayRecord, holiday, isWeekend, isToday, isFuture, status, color, isFiltered }, idx) => (
                                    <React.Fragment key={d}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            py: 1.5,
                                            pl: 1.5,
                                            pr: 1,
                                            borderLeft: `4px solid ${color}`,
                                            bgcolor: alpha(color, 0.06),
                                            opacity: isFiltered ? 0.2 : 1,
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 'bold', width: 32, height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                                                    bgcolor: isToday ? theme.palette.primary.main : 'transparent',
                                                    color: isToday ? 'white' : (holiday ? theme.palette.secondary.main : isWeekend ? theme.palette.text.disabled : 'text.primary'),
                                                    fontSize: '0.8rem',
                                                }}>
                                                    {d}
                                                </Typography>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight="bold" noWrap>{currentLoopDate.format('dddd')}</Typography>
                                                    {holiday ? (
                                                        <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontWeight: 'bold', display: 'block', wordBreak: 'break-word' }}>
                                                            {holiday.holiday_name}
                                                        </Typography>
                                                    ) : isWeekend ? (
                                                        <Typography variant="caption" color="text.disabled">Weekend</Typography>
                                                    ) : isFuture ? (
                                                        <Typography variant="caption" color="text.disabled">Upcoming</Typography>
                                                    ) : null}

                                                    {!isFuture && !isWeekend && !holiday && (
                                                        <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {dayRecord?.inOffice ? (
                                                                    <LocationOn sx={{ fontSize: 14, color: '#2e7d32' }} />
                                                                ) : (
                                                                    <LocationOff sx={{ fontSize: 14, color: theme.palette.error.main }} />
                                                                )}
                                                                <Box>
                                                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>Check-In</Typography>
                                                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 'bold', lineHeight: 1.3 }}>{dayRecord?.inTime || '--:--'}</Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {dayRecord?.outOffice ? (
                                                                    <LocationOn sx={{ fontSize: 14, color: '#2e7d32' }} />
                                                                ) : (
                                                                    <LocationOff sx={{ fontSize: 14, color: dayRecord?.outTime === '--:--' ? theme.palette.text.disabled : theme.palette.error.main }} />
                                                                )}
                                                                <Box>
                                                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>Check-Out</Typography>
                                                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 'bold', lineHeight: 1.3 }}>{dayRecord?.outTime || '--:--'}</Typography>
                                                                </Box>
                                                            </Box>
                                                        </Stack>
                                                    )}
                                                </Box>
                                            </Box>

                                            {!isFuture && !isWeekend && !holiday && (
                                                <Box sx={{ flexShrink: 0, pt: 0.5 }}>{getStatusIcon(status)}</Box>
                                            )}
                                        </Box>
                                        {idx < dayItems.length - 1 && <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </>
                    );
                })()}
            </Paper>
        </Box>
    );
}

export default MyAttendance;